<template>
  <div
    class="supplier-decode"
    :class="{ 'supplier-decode-light': theme === 'light' }"
  >
    <img :src="require(`@/assets/images/hall/${imgName}.png`)" alt="" />
    <div class="decode-tip">{{ decryptTip }}</div>
    <el-button v-if="pendingDecode" type="primary" @click="decodeFile">
      点击解密
    </el-button>
    <el-button v-else-if="afterDecode" type="primary" @click="refreshResult">
      刷新结果
    </el-button>

    <decrypt-flow
      ref="decryptFlow"
      :encrypt-file-id="fileId"
      :bidder-id="bidderId"
      :project-id="projectId"
      is-supplier
      :review-model="reviewModel"
      @getResult="getDecryptResult"
    />
  </div>
</template>

<script>
import DecryptFlow from '@/components/common/DecryptFlow.vue'
import { mapState } from 'vuex'
import {
  fileDecryptionStatusDict,
  quoteOpenStatusDict
} from '@/dictionary/hall/open-bid-hall'
import {
  bidFileOpenStatsDict,
  bidFileTypeDict,
  reviewModelDict
} from '@/dictionary/project/kpb-project'
import { isAfter } from 'date-fns'
import DateUtils from '@/utils/date'

let decryptFileTimer = null

/**
 * 供应商解密操作内容
 */
export default {
  name: 'BidOpenSupplierDecrypt',

  components: {
    DecryptFlow
  },

  props: {
    // 项目id
    projectId: {
      type: [String, Number],
      default: 0
    },

    // 投标单位id
    bidderId: {
      type: [String, Number],
      default: 0
    },

    // 投标文件类型
    bidFileType: {
      type: [String, Number],
      default: bidFileTypeDict.BUSINESS.value
    },

    // 评审模式
    reviewModel: {
      type: [String, Number],
      default: reviewModelDict.DEFAULT.value
    },

    theme: {
      validator: function(value) {
        return ['dark', 'light'].indexOf(value) !== -1
      },
      default: 'dark'
    },

    // 文件解密相关信息
    fileDecryptInfo: {
      type: Object,
      default: () => {}
    },

    // 商务文件开标状态
    bidOpenStatus: {
      type: Number,
      default: 0
    },

    // 报价文件开标状态
    offerOpenStatus: {
      type: Number,
      default: 0
    },

    // 文件解密设置
    fileDecryptConfig: {
      type: Object,
      default: () => {}
    }
  },

  data() {
    return {
      imgName: 'lock-un-decode',
      decryptTip: ''
    }
  },

  computed: {
    ...mapState('systemTime', ['staticDate']),

    // 加密文件id，用于解密
    fileId() {
      return this.fileDecryptInfo.fileId || 0
    },

    // 未开启商务文件解密
    beforeStartDecrypt() {
      return this.bidOpenStatus === bidFileOpenStatsDict.PENDING.value
    },

    // 未开启报价文件解密
    beforeStartQuoteDecrypt() {
      return this.offerOpenStatus === quoteOpenStatusDict.PENDING.value
    },

    // 是双信封
    isDouble() {
      return this.reviewModel === reviewModelDict.DOUBLE.value
    },

    // 是商务文件解密
    isBidDecrypt() {
      return this.bidFileType === bidFileTypeDict.BUSINESS.value
    },

    // 是报价文件解密
    isQuoteDecrypt() {
      return this.bidFileType === bidFileTypeDict.QUOTATION.value
    },

    // 等待解密
    pendingDecode() {
      // 如果是报价文件解密，则开启解密前，不显示
      if (this.isQuoteDecrypt && this.beforeStartQuoteDecrypt) {
        return false
      }

      // 如果是双信封，则在开启解密前，不显示
      if (this.isDouble && this.isBidDecrypt && this.beforeStartDecrypt) {
        return false
      }

      // 解密时间不存在，则待解密
      return !this.afterDecode
    },

    // 解密后
    afterDecode() {
      return this.fileDecryptInfo.decryptTime
    },

    // 解密文案
    decryptTipMap() {
      const tipMap = {
        [bidFileTypeDict.BUSINESS.value]: {
          // 开启解密前文案
          beforeOpenTip: '未开启解密，请等待采购人开启文件解密',
          // 提示解密文案
          beforeDecryptTip: '已开启解密，请解密您的投标文件',
          // 解密后文案
          afterDecryptTip: '文件已解密，请耐心等待开标结果',
          // 解密失败文案
          decryptFailTip: '文件解密失败，请重新解密',
          // 解密成功文案
          decryptSuccessTip: '文件已解密，请耐心等待开标结果'
        },
        [bidFileTypeDict.QUOTATION.value]: {
          // 开启解密前文案
          beforeOpenTip: '未开启解密，请等待采购人开启文件解密',
          // 提示解密文案
          beforeDecryptTip: '已开启报价解密，请在规定时间内解密您的报价文件',
          // 解密后文案
          afterDecryptTip: '报价文件已解密，请耐心等待报价开标结果',
          // 解密失败文案
          decryptFailTip: '报价文件解密失败，请重新解密',
          // 解密成功文案
          decryptSuccessTip: '报价文件已解密，请耐心等待报价开标结果'
        }
      }

      return tipMap[this.bidFileType]
    }
  },

  watch: {
    bidOpenStatus(val) {
      this.initTip()
    },

    offerOpenStatus(val) {
      this.initTip()
    }
  },

  mounted() {
    this.$store.dispatch('systemTime/getSystemTime')
    // 监听用户离开和回到页面，触发函数，更新数据
    document.addEventListener('visibilitychange', this.visibilityChange)
    // 通过hook监听组件销毁钩子函数
    this.$once('hook:beforeDestroy', () => {
      document.removeEventListener('visibilitychange', this.visibilityChange)
    })
    this.visibilityChange()
    this.initTip()
  },

  methods: {
    initTip() {
      if (!this.isDouble) {
        this.decryptTip = this.decryptTipMap.beforeDecryptTip
      } else {
        // 如果是双信封，则要增加设置解密时间限制
        this.decryptTip = this.pendingDecode
          ? this.decryptTipMap.beforeDecryptTip
          : this.decryptTipMap.beforeOpenTip
      }
      if (this.afterDecode) {
        this.decryptTip = this.decryptTipMap.afterDecryptTip
        this.imgName = 'lock-decode'
      }
    },

    /**
     * 离开该窗口页面(即离开当前标签页)，再次回来触发的事件函数
     */
    visibilityChange() {
      clearInterval(decryptFileTimer)
      decryptFileTimer = null
    },

    /**
     * 文件解密
     */
    decodeFile() {
      // 双信封模式，解密时间已过，但仍未解密的，则不能进行解密
      if (this.isDouble && this.fileDecryptConfig.decryptExpired) {
        this.$message.warning('文件解密时间已截至，无法解密')
        return
      }
      this.$refs.decryptFlow
        .checkDecryptResult({
          showToast: false,
          bidFileType: this.bidFileType
        })
        .then(res => {
          if (res.data?.status !== fileDecryptionStatusDict.SUCCESS.value) {
            this.$refs.decryptFlow.caTypeDialogVisible = true
          }
        })
    },

    /**
     * 刷新开标结果
     */
    refreshResult() {
      this.$emit('refresh')
    },

    /**
     * 查询业务端解密结果
     */
    getDecryptResult(decryptStatus) {
      if (decryptStatus === fileDecryptionStatusDict.FAIL.value) {
        this.$message.error(this.decryptTipMap.decryptFailTip)
        this.decryptTip = this.decryptTipMap.decryptFailTip
        this.imgName = 'lock-un-decode'
        this.refreshResult()
      }

      if (decryptStatus === fileDecryptionStatusDict.SUCCESS.value) {
        this.decryptTip = this.decryptTipMap.decryptSuccessTip
        this.imgName = 'lock-decode'
        this.refreshResult()
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.supplier-decode {
  width: 100%;
  height: 100%;
  padding-top: 200px;
  display: flex;
  flex-direction: column;
  align-items: center;
  overflow: hidden;

  &.supplier-decode-light {
    .decode-tip {
      color: $text-color;
    }
  }

  img {
    width: 160px;
    height: 130px;
  }

  .decode-tip {
    font-size: 24px;
    color: #feffff;
    margin: 30px 0;
  }

  // 解密按钮样式
  ::v-deep .el-button {
    width: 160px;
    font-size: 22px !important;
    span {
      letter-spacing: 0.2em;
    }
  }
  &:not(.supplier-decode-light) {
    ::v-deep .el-button {
      color: #061b38;
      border-color: transparent !important;

      &.el-button--primary {
        background: #27d8fe !important;
      }
    }
  }
}
</style>
